import { useCallback } from "react";
import { useTranslation } from "react-i18next";

import { type BackgroundJobType } from "~/gql/graphqlBase.generated";
import useMutationRunner from "~/hooks/useMutationRunner";
import {
	useCancelBackgroundJobMutation,
	useClearFinishedBackgroundJobsMutation,
	useDismissBackgroundJobMutation,
	useRetryFailedBackgroundJobItemsMutation
} from "~/services/endpoints/apiEndpoints";

export interface UseBackgroundJobMutationsReturn {
	cancelBackgroundJob: (jobId: string) => Promise<boolean>;
	clearFinishedBackgroundJobs: () => Promise<boolean>;
	dismissBackgroundJob: (jobId: string, type?: BackgroundJobType | null) => Promise<boolean>;
	retryFailedBackgroundJobItems: (jobId: string) => Promise<boolean>;
}

const useBackgroundJobMutations = (): UseBackgroundJobMutationsReturn => {
	const { t } = useTranslation("common");
	const { run } = useMutationRunner();
	const [cancelMutation] = useCancelBackgroundJobMutation();
	const [retryMutation] = useRetryFailedBackgroundJobItemsMutation();
	const [dismissMutation] = useDismissBackgroundJobMutation();
	const [clearMutation] = useClearFinishedBackgroundJobsMutation();
	const errorMessage = t("backgroundJobs.actionFailed");

	const cancelBackgroundJob = useCallback(
		(jobId: string) => run(cancelMutation({ jobId }), (data) => data.cancelBackgroundJob, errorMessage),
		[cancelMutation, errorMessage, run]
	);

	const retryFailedBackgroundJobItems = useCallback(
		(jobId: string) => run(retryMutation({ jobId }), (data) => data.retryFailedBackgroundJobItems, errorMessage),
		[errorMessage, retryMutation, run]
	);

	const dismissBackgroundJob = useCallback(
		(jobId: string, type?: BackgroundJobType | null) =>
			run(dismissMutation({ jobId, type }), (data) => data.dismissBackgroundJob, errorMessage),
		[dismissMutation, errorMessage, run]
	);

	const clearFinishedBackgroundJobs = useCallback(
		() => run(clearMutation(), (data) => data.clearFinishedBackgroundJobs, errorMessage),
		[clearMutation, errorMessage, run]
	);

	return {
		cancelBackgroundJob,
		clearFinishedBackgroundJobs,
		dismissBackgroundJob,
		retryFailedBackgroundJobItems
	};
};

export default useBackgroundJobMutations;
